import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';

import { useVisibilityContext } from './VisibilityProvider';

type Term = {
  label: string;
  score: number;
  variation?: number;
};

interface TopAttackedSearchTermsProps {
  terms?: Term[];
  loading?: boolean;
  barColor?: string;
  onSelectTerm?: (term: string) => void;
  onResetFilter?: () => void;
  isClientDomain?: boolean;
}

const PAGE_SIZE = 5;

export default function TopAttackedSearchTerms({
  terms,
  loading = false,
  barColor = '#C09910',
  onSelectTerm,
  onResetFilter,
  isClientDomain = false,
}: TopAttackedSearchTermsProps) {
  const { t } = useLocales();
  const { filterStore } = useVisibilityContext();

  const [page, setPage] = useState(0);

  const fetchedTerms: Term[] = useMemo(() => {
    const keywords = filterStore?.loadedKeywords as
      | Array<{
        search_term: string;
        total_organic_searches: number;
        variation?: number;
        variation_pp?: number;
        paid_presence_rate_compare?: number;
      }>
      | undefined;

    if (!keywords?.length) {
      return [];
    }

    const slicedKeywords = [...keywords]
      .sort(
        (a, b) =>
          (b.total_organic_searches ?? 0) -
          (a.total_organic_searches ?? 0)
      )
      .slice(0, 200);

    const maxValue = Math.max(
      0,
      ...slicedKeywords.map((keyword) =>
        typeof keyword.total_organic_searches === 'number'
          ? keyword.total_organic_searches
          : 0
      )
    );

    return slicedKeywords.map((keyword) => {
      const rawScore =
        typeof keyword.total_organic_searches === 'number'
          ? keyword.total_organic_searches
          : 0;

      return {
        label: keyword.search_term,
        score:
          maxValue > 0
            ? Math.round((rawScore * 100) / maxValue)
            : 0,
        variation:
          keyword.variation_pp ??
          keyword.variation ??
          keyword.paid_presence_rate_compare ??
          0,
      };
    });
  }, [filterStore?.loadedKeywords]);

  const data = useMemo(() => {
    const source = terms ?? [];

    return source.filter((term) => (term?.score ?? 0) >= 1);
  }, [terms]);

  const selectedTerms = useMemo(() => {
    const keywords = filterStore?.keywordsPLA as string[] | undefined;
    return keywords ?? [];
  }, [filterStore?.keywordsPLA]);

  const totalItems = data.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const startIndex = page * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalItems);

  const visibleTerms = useMemo(
    () => data.slice(startIndex, endIndex),
    [data, startIndex, endIndex]
  );

  useEffect(() => {
    setPage(0);
  }, [data]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const title = t(
    isClientDomain
      ? 'dashboard.global.visibility.top_search_terms'
      : 'dashboard.global.visibility.top_attacked_search_terms'
  );

  const titleTooltip = t(
    isClientDomain
      ? 'definitions.top_search_terms'
      : 'definitions.top_attacked_search_terms'
  );

  const handlePreviousPage = () => {
    setPage((currentPage) => Math.max(0, currentPage - 1));
  };

  const handleNextPage = () => {
    setPage((currentPage) =>
      Math.min(totalPages - 1, currentPage + 1)
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '12px',
        borderColor: 'rgba(145, 158, 171, 0.20)',
        boxShadow: 'none',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          minHeight: 56,
          px: 2.5,
          gap: 2,
        }}
      >
        <Tooltip placement="top" arrow title={titleTooltip}>
          <Typography
            sx={{
              color: '#333C47',
              fontFamily: '"Public Sans", sans-serif',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '22px',
              cursor: 'help',
              whiteSpace: 'nowrap',
            }}
          >
            {title} by Paid Presence Rate
          </Typography>
        </Tooltip>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            flexShrink: 0,
          }}
        >
          {selectedTerms.length > 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minHeight: 36,
                  px: 1.25,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(145, 158, 171, 0.08)',
                }}
              >
                <Typography
                  sx={{
                    color: '#333C47',
                    fontFamily: '"Public Sans", sans-serif',
                    fontSize: '13px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '23px',
                  }}
                >
                  {selectedTerms.length}
                </Typography>

                <Typography
                  sx={{
                    color: '#333C47',
                    fontFamily: '"Public Sans", sans-serif',
                    fontSize: '13px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '23px',
                  }}
                >
                  Selected
                </Typography>
              </Box>

              {onResetFilter && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  role="button"
                  tabIndex={0}
                  onClick={onResetFilter}
                  onKeyDown={(event) => {
                    if (
                      event.key === 'Enter' ||
                      event.key === ' '
                    ) {
                      onResetFilter();
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    color: '#333C47',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M12.75 5.25L5.25 12.75M5.25 5.25L12.75 12.75" stroke="#5E6D7B" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>

                  <Typography
                    sx={{
                      color: '#333C47',
                      fontFamily: '"Public Sans", sans-serif',
                      fontSize: '13px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '23px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Clear all
                  </Typography>
                </Stack>
              )}
            </>
          )}

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 0.5,
              borderColor: 'rgba(145, 158, 171, 0.32)',
            }}
          />
          <Typography
            sx={{
              color: '#919EAB',
              fontFamily: '"Public Sans", sans-serif',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '22px',
              whiteSpace: 'nowrap',
            }}
          >
            {totalItems === 0
              ? '0'
              : `${startIndex + 1}–${endIndex}`}{' '}
            of {totalItems}
          </Typography>

          <IconButton
            size="small"
            disabled={page === 0}
            onClick={handlePreviousPage}
            aria-label="Previous search terms page"
            sx={{
              p: 0.5,
            }}
          >
            <Iconify
              icon="eva:arrow-ios-back-outline"
              width={20}
            />
          </IconButton>

          <IconButton
            size="small"
            disabled={
              totalItems === 0 || page >= totalPages - 1
            }
            onClick={handleNextPage}
            aria-label="Next search terms page"
            sx={{
              p: 0.5,
            }}
          >
            <Iconify
              icon="eva:arrow-ios-forward-outline"
              width={20}
            />
          </IconButton>
        </Stack>
      </Stack>

      <Divider
        sx={{
          borderColor: 'rgba(145, 158, 171, 0.20)',
        }}
      />

      {loading ? (
        <Stack sx={{ py: 1 }}>
          {Array.from({ length: PAGE_SIZE }).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  visibleTerms.some((item) => item.variation !== undefined)
                    ? '28px minmax(130px, 165px) 50px auto minmax(180px, 1fr)'
                    : '28px minmax(130px, 165px) 50px minmax(180px, 1fr)',
                alignItems: 'center',
                // columnGap: 1,
                minHeight: 36,
                px: 2.5,
              }}
            >
              {/* Checkbox */}
              <Skeleton
                variant="rounded"
                width={22}
                height={22}
                sx={{
                  borderRadius: '6px',
                }}
              />

              {/* Search term */}
              <Skeleton
                variant="text"
                width="80%"
                height={22}
              />

              {/* Pourcentage */}
              <Skeleton
                variant="text"
                width={36}
                height={22}
                sx={{
                  justifySelf: 'end',
                }}
              />

              {/* Progress bar */}
              <Skeleton
                variant="rounded"
                width="100%"
                height={9}
                sx={{
                  ml: "12px",
                  borderRadius: 999,
                }}
              />
            </Box>
          ))}
        </Stack>
      ) : (
        <Stack sx={{ py: 1 }}>
          {visibleTerms.map((term) => {
            const isSelected = selectedTerms.includes(term.label);
            const variation = term.variation ?? 0;

            return (
              <Box
                key={term.label}
                onClick={
                  onSelectTerm
                    ? () => onSelectTerm(term.label)
                    : undefined
                }
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    visibleTerms.some((item) => item.variation !== undefined)
                      ? '28px minmax(130px, 165px) 50px minmax(180px, 1fr) 56px'
                      : '28px minmax(130px, 165px) 50px minmax(180px, 1fr)',
                  columnGap: 1,
                  minHeight: 36,
                  alignItems: 'center',

                  px: 2.5,
                  cursor: onSelectTerm
                    ? 'pointer'
                    : 'default',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor:
                      'rgba(145, 158, 171, 0.04)',
                  },
                }}
              >
                <Checkbox
                  checked={isSelected}
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();

                    if (onSelectTerm) {
                      onSelectTerm(term.label);
                    }
                  }}
                  sx={{
                    p: 0,
                    width: 22,
                    height: 22,
                    color: '#DFE3E8',
                    '&.Mui-checked': {
                      color: '#8E5BEF',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 24,
                    },
                  }}
                />

                <Tooltip
                  title={term.label}
                  placement="top"
                  arrow
                  disableInteractive
                >
                  <Typography
                    sx={{
                      minWidth: 0,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      color: '#333C47',
                      fontFamily:
                        '"Public Sans", sans-serif',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '22px',
                    }}
                  >
                    {term.label}
                  </Typography>
                </Tooltip>

                <Tooltip
                  title={t(
                    'dashboard.global.paid_presence_rate'
                  )}
                  placement="top"
                  arrow
                  disableInteractive
                >
                  <Typography
                    sx={{
                      color: '#333C47',
                      cursor: 'help',
                      fontFamily:
                        '"Public Sans", sans-serif',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '22px',
                      textAlign: 'right',
                    }}
                  >
                    {term.score}%
                  </Typography>
                </Tooltip>

                <Box
                  sx={{
                    width: '100%',
                    height: 9,
                    overflow: 'hidden',
                    borderRadius: 999,
                    ml: "12px",
                    backgroundColor:
                      'transparent',
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(
                        100,
                        Math.max(0, term.score)
                      )}%`,
                      height: '100%',
                      borderRadius: 999,
                      backgroundColor: barColor,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>


                {term.variation !== undefined && (
                  <Typography
                    sx={{
                      minWidth: 56,
                      textAlign: 'right',
                      color:
                        term.variation > 0
                          ? 'rgba(24, 161, 85, 1)'
                          : term.variation < 0
                            ? 'rgba(220, 24, 24, 1)'
                            : '#637381',
                      fontSize: '13px',

                      whiteSpace: 'nowrap',
                    }}
                  >
                    {term.variation > 0 ? '+' : ''}
                    {term.variation.toFixed(0)}pp
                  </Typography>
                )}

              </Box>
            );
          })}

          {visibleTerms.length === 0 && (
            <Box
              sx={{
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  color: '#919EAB',
                  fontFamily:
                    '"Public Sans", sans-serif',
                  fontSize: '14px',
                }}
              >
                0 results found
              </Typography>
            </Box>
          )}
        </Stack>
      )}
    </Paper>
  );
}